<template>
  <transition name="fade">
    <div
      v-show="isVisible"
      :id="id"
      ref="tooltipEl"
      :aria-hidden="!isVisible"
      class="ui-tooltip"
      data-test="ui-tooltip"
    >
      <div
        :id="`${id}__arrow`"
        ref="arrowEl"
        class="ui-tooltip__arrow"
      >
        <img
          alt=""
          :src="arrowImage"
        >
      </div>
      <p class="ui-tooltip__text">
        <slot>
          {{ text }}
        </slot>
      </p>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { computePosition, autoUpdate, flip, shift, offset, arrow, type ComputePositionConfig, type ComputePositionReturn, type Placement } from '@floating-ui/dom'
import arrowImage from '../../assets/images/tooltip-arrow.svg'

interface UiTooltipProps {
  /** The `id` of the element that is being described */
  for: string
  /**
   * The text that will display inside of the tooltip
   * @deprecated please use `default slot` instead, the `text` prop will be removed later.
   * @example ```<UiTooltip>This is the default slot content.`</UiTooltip>```
   */
  text?: string
  /** Which side the tooltip should display on. */
  placement?: Placement
  /** The offset between the tooltip and the element it's attached to. */
  offsetSize?: number
}

defineSlots<{
  default: () => any
}>()

const props = withDefaults(defineProps<UiTooltipProps>(), {
  text: undefined,
  placement: 'bottom',
  offsetSize: 12,
})

const id = ref(`${props.for}-tooltip`)
const tooltipEl = ref<HTMLDivElement>()
const buttonEl = ref<HTMLButtonElement>()
const arrowEl = ref<HTMLDivElement>()
const isVisible = ref(false)
const options = ref<Partial<ComputePositionConfig> | null>(null)
const cleanup = ref<Function | null>(null)

onMounted(() => {
  buttonEl.value = document.querySelector<HTMLButtonElement>(`#${props.for}`) || undefined
  buttonEl.value?.setAttribute('aria-describedby', id.value)

  if (arrowEl.value) {
    options.value = {
      placement: props.placement,
      middleware: [
        offset(props.offsetSize),
        flip(),
        shift({ padding: 16 }),
        arrow({
          element: arrowEl.value,
          padding: 8,
        }),
      ],
    }
  }

  if (buttonEl.value && tooltipEl.value) {
    cleanup.value = autoUpdate(buttonEl.value, tooltipEl.value, () => {
      if (buttonEl.value && tooltipEl.value && options.value) {
        computePosition(buttonEl.value, tooltipEl.value, options.value).then(positionTooltip)
      }
    })
  }
})

onUnmounted(() => {
  if (cleanup.value) {
    cleanup.value()
  }
})

function positionTooltip({ x, y, placement, middlewareData }: ComputePositionReturn): void {
  if (tooltipEl.value) {
    Object.assign(tooltipEl.value.style, {
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  const arrowX = middlewareData.arrow?.x
  const arrowY = middlewareData.arrow?.y

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]]

  if (arrowEl.value && staticSide) {
    Object.assign(arrowEl.value.style, {
      left: arrowX !== undefined ? `${arrowX}px` : '',
      top: arrowY !== undefined ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-10px',
    })
  }

  const events: [string, EventListener][] = [
    ['mouseenter', showTooltip],
    ['mouseleave', hideTooltip],
    ['focus', showTooltip],
    ['blur', hideTooltip],
  ]

  events.forEach(([event, listener]) => {
    buttonEl.value?.addEventListener(event, listener)
  })
}

function showTooltip(): void {
  isVisible.value = true
  document.addEventListener('keydown', handlePressEscape)
}

function hideTooltip(): void {
  isVisible.value = false
  document.removeEventListener('keydown', handlePressEscape)
}

function handlePressEscape(event: KeyboardEvent): void {
  if (event.keyCode == 27) {
    hideTooltip()
    buttonEl.value?.blur()
  }
}
</script>

<style lang="scss" scoped>
@use '../../styles/components/ui-tooltip/ui-tooltip';

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.125s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
